/**
 * senchado-collection-dougu — 道具(茶器)ディスカバリー エンジン
 *
 * 設計: docs/spec-dougu-discovery.md（3AI統合・2026-07-01確定）＋2026-07-02 谷本フィードバック反映。
 * teaエンジン(senchado-collection.js)の実証済みパターン
 *   ・Section Rendering ページング（50件超の残り取得）
 *   ・在庫あり優先パーティション＋stableソート
 *   ・/cart/add.js ＋ cart:updated イベント
 * を「グラム/容器/5軸カルーセル」のtea結合なしで移植した専用実装。
 *
 * 二軸ディスカバリー（用途×作家）:
 *   - 用途: settings.scdougu 由来のピル（Liquidが描画・JSが件数/active/dim を注釈）。
 *     クリックでクライアント側絞込。?filter.p.tag=scdougu_* 深リンクも boot 時に
 *     同じ絞込へ変換する（Storefront Filtering はこのストアでは未設定＝サーバー側では効かない。
 *     2026-07-02 実測: 本番/プレビューとも filter.p.tag はサーバー側で無反応）。
 *   - 作家: artisan MO由来のレール。data-artisan-action="filter"(既定) はin-pageフィルタ、
 *     "navigate" は実コレクション(/collections/{slug})へ遷移（作家コレクション作成後に切替）。
 *
 * 残ページ取得は直列バッチ(3並列)＋1リトライ。ページ数はサーバー申告値(paginate.pages)が正
 * （by: 50 指定でも page_size=20 にクランプされる実測あり・50仮定は欠落事故のもと）。
 *
 * 作家カード点数の定義（固定）: その作家の「在庫あり」商品数（現在の用途絞込内）。
 */
(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(str) { return escapeHtml(str); }

  var UNCLASSIFIED = '未分類';

  /**
   * 検索の正規化（2026-08-05）。
   * 「あべ」で 阿部春弥 を、「アベ」でも「ABE」でも「abe」でも引けるようにする。
   *   全角英数→半角 / カタカナ→ひらがな / 大文字→小文字 / 区切り記号と空白を落とす
   * 落とす記号に長音「ー」を含めるのは、シャンブリートリオ のような表記を
   * 「しゃんぶりとりお」と打っても引けるようにするため（両辺に同じ処理を掛けるので破綻しない）。
   */
  function normalizeText(str) {
    if (!str) return '';
    var out = String(str).toLowerCase();
    // 全角英数記号 → 半角
    out = out.replace(/[！-～]/g, function (c) {
      return String.fromCharCode(c.charCodeAt(0) - 0xFEE0);
    });
    // カタカナ → ひらがな（ヷヸヹヺ は対象外・実データに無い）
    out = out.replace(/[ァ-ヶ]/g, function (c) {
      return String.fromCharCode(c.charCodeAt(0) - 0x60);
    });
    // 空白・中黒・各種ダッシュ・括弧・句読点を落とす
    return out.replace(/[\s　・･\-‐‑–—ー_.,、。/()（）「」【】&]/g, '');
  }

  /**
   * 作家索引（読み仮名・50音行・リンク用タグ・あいうえお順の順位）。
   * 供給元 = snippets/senchado-artisan-index.liquid（scripts/artisan-yomi.json から自動生成）。
   * 読み仮名は artisan MO に無いため、ここが唯一の供給路。取得できなくても
   * 検索・並び順は「名前だけ」で動く（degraded だが壊れない）。
   */
  function readArtisanIndex(root) {
    var el = (root && root.querySelector('[data-senchado-artisan-index]')) ||
             document.querySelector('[data-senchado-artisan-index]');
    if (!el) return { rows: [], artisans: {} };
    try {
      var parsed = JSON.parse(el.textContent || '{}');
      return { rows: parsed.rows || [], artisans: parsed.artisans || {} };
    } catch (e) {
      if (window.console) console.warn('[senchado-dougu] 作家索引の読み込みに失敗:', e);
      return { rows: [], artisans: {} };
    }
  }

  // 窓表示: 初期表示件数＝VISIBLE_STEP(24)。以降は「もっと見る」ボタン1回押下ごとに LOAD_STEP(40) を加算。
  // 全件はバックグラウンドで DOM に載る（作家集計・ピル件数は全件索引が前提）が、表示だけ窓化する。
  // 自動無限スクロールはやらない＝ボタン逐次方式（押すたびにバッと出る・残があれば毎回ボタン再表示）。
  // 2026-07-07 谷本実機決裁: IO自動追加はボタンが小さく気づきにくかった → ボタン逐次＋大型化へUX変更。
  var VISIBLE_STEP = 24; // 初期表示・絞込/ソート/在庫トグル/クリア時のリセット窓
  var LOAD_STEP = 40;    // 「もっと見る」1回で増える件数

  // ---------- ENGINE ----------
  function DouguEngine(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || '';
    this.collectionHandle = root.dataset.collectionHandle || '';
    this.totalCount = parseInt(root.dataset.totalCount, 10) || 0;
    // ページ数はサーバー申告値（paginate.pages）が正。
    // 2026-07-02 実測: paginate by: 50 でも page_size=20 にクランプされる（プラットフォーム挙動）
    // → 「50件/ページ」仮定で ceil(total/50) すると後半ページが欠落する（180/424 事故）。
    this.totalPages = parseInt(root.dataset.pages, 10) || Math.ceil(this.totalCount / 50);
    this.artisanAction = root.dataset.artisanAction || 'filter';
    // 作家リンクのベース（ハードコード回避・道具コレクションの .url を Liquid が入れる）
    this.artisanLinkBase = root.dataset.artisanLinkBase || '/collections/dougu';

    // 読み仮名・50音行・リンク用タグ・あいうえお順の順位
    var idx = readArtisanIndex(root);
    this.artisanIndex = idx.artisans;
    this.kanaRows = idx.rows;

    this.grid = root.querySelector('[data-dougu-grid]');
    this.emptyState = root.querySelector('[data-empty]');
    this.loadingEl = root.querySelector('[data-loading]');
    this.artisanRail = root.querySelector('[data-artisan-rail]');
    this.artisanSection = root.querySelector('[data-artisan-section]');
    this.purposeRail = root.querySelector('[data-purpose-rail]');
    this.sozaiRail = root.querySelector('[data-sozai-rail]');
    this.visibleCountEl = root.querySelector('[data-visible-count]');
    this.sortEl = root.querySelector('[data-sort]');
    this.availableOnlyEl = root.querySelector('[data-available-only]');
    this.bcLeaf = root.querySelector('[data-bc-leaf]');
    this.bcSep = root.querySelector('[data-bc-sep]');
    this.loadMoreEl = root.querySelector('[data-load-more]');
    this.loadMoreCountEl = root.querySelector('[data-load-more-count]');
    this.searchEl = root.querySelector('[data-dougu-search]');
    this.searchClearEl = root.querySelector('[data-search-clear]');
    this.kanaBar = root.querySelector('[data-kana-bar]');
    this.artisanSortEl = root.querySelector('[data-artisan-sort]');

    this.sortMode = 'default';
    // 既定の並びを日替わりにするか。道具の一覧だけ ON（セクション設定）。
    // senchado-shuffle.js が読めない環境では黙って従来の新着順に落ちる。
    this.dailyOrder = root.dataset.dailyOrder === 'true'
      && typeof window !== 'undefined' && !!window.SenchadoShuffle;
    this.collectionHandle = root.dataset.collectionHandle || '';
    this.availableOnly = false;
    this.activePurpose = null; // 用途ラベル or '未分類' or null
    this.activeMaterial = null; // 素材ラベル or '未分類' or null
    this.activeArtisan = null; // 作家slug or null
    this.activeKanaRow = null; // 50音行ラベル('あ'等) or null
    this.searchTokens = []; // 正規化済みの検索語（全語 AND）
    // 作家レールの並び: 'kana'=あいうえお順（既定）/ 'stock'=在庫の多い順
    // 2026-08-05 谷本決裁: 既定を在庫順からあいうえお順へ。96名の横スクロールでは
    // 在庫順だと目的の作家に辿り着けなかったため（実測13,040px＝約11画面分）。
    this.artisanSort = 'kana';
    this.products = [];

    // 窓表示状態: visibleLimit 件だけ実表示。「もっと見る」ボタン押下ごとに LOAD_STEP を加算する
    // （自動無限スクロールはやらない＝スクロールでは増えない・押下時のみ増える）。
    this.visibleLimit = VISIBLE_STEP;

    this.init();
  }

  DouguEngine.prototype.init = function () {
    var self = this;
    var rendered = this.grid ? this.grid.querySelectorAll('[data-card]').length : 0;

    // A案（2026-07-02 谷本FB: 表示速度）: 1ページ目(20件)で即起動し操作可能にする。
    // 素材/用途ピル・作家レールも即描画（暫定件数）。残ページは裏で「1ページ届くたびに追記＋
    // グリッド即再描画」するプログレッシブ方式（2026-07-05: 437商品/約22ページで全ページ揃うまで
    // 1枚も出さない all-or-nothing だと「20件で固まって見える／全件出るまで数十秒」問題があったため）。
    // 作家レール・ピルの件数は負荷とちらつき抑制のため全ページ完了時に一括確定。絞込状態は保持。
    this.start();

    if (this.grid && rendered < this.totalCount) {
      this.root.dataset.backfill = 'loading';
      if (this.loadingEl) this.loadingEl.classList.add('is-visible');
      this.fetchRemainingPages().then(function () {
        if (self.loadingEl) self.loadingEl.classList.remove('is-visible');
        self.root.dataset.backfill = 'done';
      }).catch(function (err) {
        if (window.console) console.warn('[senchado-dougu] 追加ページ取得失敗:', err);
        if (self.loadingEl) self.loadingEl.classList.remove('is-visible');
        self.root.dataset.backfill = 'error';
      });
    } else {
      this.root.dataset.backfill = 'done';
    }
  };

  // 残ページ取得（プログレッシブ）: ワーカープール(並列5)で順不同に取得し、1ページ届くたびに
  // カードを挿入＋索引更新して即グリッド再描画する。作家レール/ピルの件数は全ページ完了時に確定。
  // 取得漏れ(スロットリング等)は末尾で1度だけ再試行し、それでも件数が足りなければ警告する
  // （437→一部欠落＝ocha 60/116級の「無言の商品欠落」を防ぐ安全網）。
  DouguEngine.prototype.fetchRemainingPages = function () {
    var self = this;
    var pages = this.totalPages;
    var nums = [];
    for (var p = 2; p <= pages; p++) nums.push(p);
    if (!nums.length) { this.refresh(); return Promise.resolve(); }

    var failed = [];

    function fetchOnce(page) {
      return self.fetchPage(page).catch(function () { return ''; });
    }

    function handlePage(page, html) {
      if (html) self.appendPageCards(html); // 挿入＋再索引（this.products を更新）
      else failed.push(page);
      self.applyView();                      // グリッド並び・可視・件数を毎ページ即反映（軽量）
      self.updateBackfillProgress();
    }

    // ワーカープール（並列5）。表示順は CSS order 制御なので取得順は不問。
    // 全ページ同時発射はスロットリング/欠落リスクがあるため上限を設ける（旧: 直列バッチ3）。
    var CONCURRENCY = 5;
    var idx = 0;
    function worker() {
      if (idx >= nums.length) return Promise.resolve();
      var page = nums[idx++];
      return fetchOnce(page).then(function (html) {
        handlePage(page, html);
        return worker();
      });
    }
    var pool = [];
    for (var w = 0; w < Math.min(CONCURRENCY, nums.length); w++) pool.push(worker());

    return Promise.all(pool).then(function () {
      // 取得漏れページを1度だけ直列で再試行（並列負荷を避けるため直列）
      if (!failed.length) return;
      var retry = failed.slice();
      failed = [];
      return retry.reduce(function (chain, page) {
        return chain.then(function () {
          return fetchOnce(page).then(function (html) {
            if (html) self.appendPageCards(html);
            else failed.push(page);
          });
        });
      }, Promise.resolve());
    }).then(function () {
      self.buildIndex();
      self.refresh(); // 件数・作家レール・ピル・グリッドを最終確定（active状態は保持）
      var got = self.grid.querySelectorAll('[data-card]').length;
      if (got < self.totalCount && window.console) {
        console.warn('[senchado-dougu] 商品欠落の可能性: 表示 ' + got + ' / 申告 ' + self.totalCount +
          (failed.length ? '（再試行後も失敗ページ: ' + failed.join(',') + '）' : ''));
      }
    });
  };

  // Section Rendering レスポンスから当該グリッドの card を取り出し、empty state の前へ一括挿入して再索引。
  DouguEngine.prototype.appendPageCards = function (html) {
    if (!html || !this.grid) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    // レスポンス内の「当該セクションの当該グリッド」配下の card のみ取り込む
    var remoteRoot = doc.querySelector('[data-senchado-dougu][data-section-id="' + this.sectionId + '"]') || doc;
    var cards = remoteRoot.querySelectorAll('[data-dougu-grid] [data-card]');
    if (!cards.length) return;
    var anchor = (this.emptyState && this.emptyState.parentNode === this.grid) ? this.emptyState : null;
    var frag = document.createDocumentFragment();
    cards.forEach(function (card) { frag.appendChild(document.importNode(card, true)); });
    if (anchor) this.grid.insertBefore(frag, anchor);
    else this.grid.appendChild(frag);
    this.buildIndex(); // 追記後に全カードを再索引（applyView/refresh が新カードを含む）
  };

  DouguEngine.prototype.updateBackfillProgress = function () {
    if (!this.loadingEl || !this.grid) return;
    var got = this.grid.querySelectorAll('[data-card]').length;
    this.loadingEl.textContent = '読み込み中… (' + got + ' / ' + this.totalCount + ')';
  };

  DouguEngine.prototype.fetchPage = function (page) {
    // 現在URLの検索条件を維持し、section_id と page だけ差し替える（preview_theme_id 等も保持）
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('page', String(page));
    return fetch(url.pathname + url.search).then(function (res) {
      return res.ok ? res.text() : '';
    });
  };

  DouguEngine.prototype.start = function () {
    this.buildIndex();
    this.applyUrlPurpose();
    // 戻る/再読込でブラウザが入力欄を復元することがある。空だと思い込まず現在値から始める
    if (this.searchEl && this.searchEl.value) this.setSearch(this.searchEl.value);
    this.bindEvents();
    this.refresh();
    this.root.dataset.engine = 'ready';
  };

  // ---------- INDEX ----------
  DouguEngine.prototype.buildIndex = function () {
    if (!this.grid) { this.products = []; return; }
    var self = this;
    var cards = this.grid.querySelectorAll('[data-card]');
    this.products = Array.prototype.map.call(cards, function (card) {
      var handle = card.dataset.artisanHandle || '';
      var meta = self.artisanIndex[handle] || null;
      var purposes = (card.dataset.purpose || '').split('|').filter(Boolean);
      var materials = (card.dataset.material || '').split('|').filter(Boolean);
      var name = card.dataset.artisanName || '';
      return {
        el: card,
        // 日替わりの並び順が使う素性（senchado-shuffle.js の契約）。
        // id は product.id。handle は使わない（日本語ハンドルのリネームで履歴が飛ぶ）
        id: card.dataset.pid || '',
        pin: card.dataset.pin ? parseInt(card.dataset.pin, 10) : null,
        wnet365: parseInt(card.dataset.wnet, 10) || 0,
        lastSold: card.dataset.lastSold || null,
        sellStart: card.dataset.sellStart || null,
        title: card.dataset.title || '',
        created: parseInt(card.dataset.created, 10) || 0,
        price: parseInt(card.dataset.price, 10) || 0,
        available: card.dataset.available === 'true',
        purposes: purposes,
        materials: materials,
        artisanSlug: card.dataset.artisanSlug || '',
        artisanHandle: handle,
        artisanName: name,
        artisanPortrait: card.dataset.artisanPortrait || '',
        artisanPrefecture: card.dataset.artisanPrefecture || '',
        artisanCraft: card.dataset.artisanCraft || '',
        artisanRow: meta ? meta.r : '',
        // 検索の干し草。作家名だけでなく読み仮名(あべ)とローマ字handle(abe-haruya)も入れる。
        // 商品名・用途・素材も同じ窓で引けるようにする（谷本決裁 2026-08-05: 窓は1つ）。
        search: normalizeText([
          card.dataset.title || '',
          name,
          meta ? meta.y : '',
          handle,
          purposes.join(' '),
          materials.join(' '),
          card.dataset.artisanPrefecture || '',
          card.dataset.artisanCraft || ''
        ].join(' '))
      };
    });
  };

  // ---------- 検索語 ----------
  // 空白区切りの全語 AND（「あべ 急須」＝阿部春弥の急須）。正規化は語ごとに掛ける
  // （normalizeText が空白を落とすため、分割してから正規化する順序が必須）。
  DouguEngine.prototype.setSearch = function (raw) {
    this.searchTokens = String(raw || '')
      .split(/[\s　]+/)
      .map(normalizeText)
      .filter(Boolean);
    if (this.searchClearEl) this.searchClearEl.hidden = !String(raw || '').length;
  };

  // ---------- 用途: URL深リンク救済 ----------
  // ヘッダー/フッターの ?filter.p.tag=scdougu_* リンクをクライアント絞込に変換する。
  // ハンドル形(scdougu_teacup)・ラベル形(scdougu_茶杯・カップ)の両形を受ける。
  DouguEngine.prototype.applyUrlPurpose = function () {
    var tag = '';
    try {
      tag = new URLSearchParams(window.location.search).get('filter.p.tag') || '';
    } catch (e) { return; }
    if (!tag) return;
    if (tag.indexOf('scdougu_') === 0 && this.purposeRail) {
      var pills = this.purposeRail.querySelectorAll('[data-purpose-pill]');
      for (var i = 0; i < pills.length; i++) {
        var label = pills[i].dataset.purposePill;
        var handle = pills[i].dataset.purposeHandle || '';
        if (tag === 'scdougu_' + handle || tag === 'scdougu_' + label) {
          this.activePurpose = label;
          return;
        }
      }
    } else if (tag.indexOf('scsozai_') === 0 && this.sozaiRail) {
      var spills = this.sozaiRail.querySelectorAll('[data-sozai-pill]');
      for (var j = 0; j < spills.length; j++) {
        var slabel = spills[j].dataset.sozaiPill;
        var shandle = spills[j].dataset.sozaiHandle || '';
        if (tag === 'scsozai_' + shandle || tag === 'scsozai_' + slabel) {
          this.activeMaterial = slabel;
          return;
        }
      }
    }
  };

  // ---------- FILTER 判定 ----------
  DouguEngine.prototype.matchesPurpose = function (p) {
    if (!this.activePurpose) return true;
    if (this.activePurpose === UNCLASSIFIED) return p.purposes.length === 0;
    return p.purposes.indexOf(this.activePurpose) !== -1;
  };

  DouguEngine.prototype.matchesMaterial = function (p) {
    if (!this.activeMaterial) return true;
    if (this.activeMaterial === UNCLASSIFIED) return p.materials.length === 0;
    return p.materials.indexOf(this.activeMaterial) !== -1;
  };

  DouguEngine.prototype.matchesArtisan = function (p) {
    return !this.activeArtisan || p.artisanSlug === this.activeArtisan;
  };

  DouguEngine.prototype.matchesKanaRow = function (p) {
    if (!this.activeKanaRow) return true;
    // 作家が付いていない商品は「か行の作家」に含まれない（＝50音で絞ると落ちる）のが正しい
    return p.artisanRow === this.activeKanaRow;
  };

  DouguEngine.prototype.matchesSearch = function (p) {
    for (var i = 0; i < this.searchTokens.length; i++) {
      if (p.search.indexOf(this.searchTokens[i]) === -1) return false;
    }
    return true;
  };

  // ---------- REFRESH（中央再描画） ----------
  DouguEngine.prototype.refresh = function () {
    var self = this;
    // 作家レール = 用途/素材/検索の絞込内で集計（作家絞込・50音は無視＝ファセット）
    var agg = this.aggregateArtisans();

    // 50音バーの件数は「その行を選んだら何人残るか」＝50音絞込を掛ける前の集計で数える
    this.renderKanaBar(agg);

    var visibleArtisans = agg.filter(function (a) {
      return !self.activeKanaRow || a.row === self.activeKanaRow;
    });
    if (this.availableOnly) {
      visibleArtisans = visibleArtisans.filter(function (a) { return a.available > 0; });
    }
    // 選択中の作家が用途変更でレールから消えたら絞込ごと解除（見えないフィルタを残さない）
    if (this.activeArtisan) {
      var slug = this.activeArtisan;
      var still = visibleArtisans.some(function (a) { return a.slug === slug; });
      if (!still) this.activeArtisan = null;
    }
    this.renderArtisanRail(visibleArtisans);
    this.renderPurposePills();
    this.renderSozaiPills();
    this.updateBreadcrumbLeaf();
    this.applyView();
  };

  // ---------- ARTISAN RAIL (作家カード) ----------
  // artisan MO のみ参照。slug+name を持つ作家を名寄せし、在庫あり点数を集計。
  DouguEngine.prototype.aggregateArtisans = function () {
    var self = this;
    var map = {};
    var order = [];
    this.products.forEach(function (p) {
      if (!p.artisanSlug || !p.artisanName) return; // 名寄せキー(slug)必須・0点作家は元から出ない
      if (!self.matchesPurpose(p)) return; // 用途絞込内で集計
      if (!self.matchesMaterial(p)) return; // 素材絞込内で集計
      if (!self.matchesSearch(p)) return; // 検索語の絞込内で集計
      var a = map[p.artisanSlug];
      if (!a) {
        var meta = self.artisanIndex[p.artisanHandle] || null;
        a = map[p.artisanSlug] = {
          slug: p.artisanSlug, handle: p.artisanHandle, name: p.artisanName, portrait: p.artisanPortrait,
          prefecture: p.artisanPrefecture, craft: p.artisanCraft, total: 0, available: 0,
          row: meta ? meta.r : '',
          // あいうえお順の順位。索引に無い作家は末尾へ（Infinity）＝並びから消えない
          order: meta ? meta.o : Infinity,
          // 作家コレクションのタグ。MO handle と乖離する作家の上書き込み
          linkTag: meta && meta.t ? meta.t : p.artisanHandle
        };
        order.push(p.artisanSlug);
      } else if (a.name !== p.artisanName && window.console) {
        // 同一 slug に別名が混在（データ不整合）。最初の名前を採用しつつ警告
        console.warn('[senchado-dougu] artisan slug 表記ゆれ:', p.artisanSlug, a.name, '≠', p.artisanName);
      }
      a.total += 1;
      if (p.available) a.available += 1;
      if (!a.portrait && p.artisanPortrait) a.portrait = p.artisanPortrait;
    });
    return order.map(function (slug) { return map[slug]; });
  };

  // 50音バー: 行ごとの作家数を出し、0名の行は dim。行を選ぶとレールとグリッドが同時に絞れる。
  // 行の集合は senchado-artisan-index の rows（＝artisan-yomi.json）が正。ここで並べ直さない。
  DouguEngine.prototype.renderKanaBar = function (agg) {
    if (!this.kanaBar || !this.kanaRows.length) return;
    var self = this;
    var counts = {};
    agg.forEach(function (a) {
      if (!a.row) return;
      if (self.availableOnly && a.available === 0) return;
      counts[a.row] = (counts[a.row] || 0) + 1;
    });

    this.kanaBar.innerHTML = this.kanaRows.map(function (row) {
      var n = counts[row.l] || 0;
      var isActive = self.activeKanaRow === row.l;
      var cls = 'senchado-dougu__kana' +
        (isActive ? ' is-active' : '') +
        (n === 0 && !isActive ? ' is-dimmed' : '');
      return '<button type="button" class="' + cls + '"' +
        ' data-kana-row="' + escapeAttr(row.l) + '"' +
        ' aria-pressed="' + (isActive ? 'true' : 'false') + '"' +
        ' aria-label="' + escapeAttr(row.l) + '行の作家（' + n + '名）">' +
        escapeHtml(row.l) +
        '<span class="senchado-dougu__kana-count">' + n + '</span>' +
        '</button>';
    }).join('');
  };

  DouguEngine.prototype.renderArtisanRail = function (list) {
    if (!this.artisanRail) return;
    var self = this;
    if (this.artisanSort === 'stock') {
      // 在庫あり点数の多い順 → 同数はあいうえお順で安定
      list = list.slice().sort(function (a, b) {
        if (b.available !== a.available) return b.available - a.available;
        return a.order - b.order || a.name.localeCompare(b.name, 'ja');
      });
    } else {
      // 既定=あいうえお順。順位は読み仮名SSOT（artisan-yomi.json）で決まっており、
      // 漢字の localeCompare では読み順にならない（作山窯/齋藤有希子…）ため使わない。
      list = list.slice().sort(function (a, b) {
        return a.order - b.order || a.name.localeCompare(b.name, 'ja');
      });
    }

    if (list.length === 0) {
      this.artisanRail.innerHTML = '';
      if (this.artisanSection) this.artisanSection.hidden = true;
      return;
    }
    if (this.artisanSection) this.artisanSection.hidden = false;

    this.artisanRail.innerHTML = list.map(function (a) {
      var isActive = (a.slug === self.activeArtisan);
      var countLabel = a.available > 0
        ? '在庫あり' + a.available + '点'
        : '在庫なし';
      var countClass = a.available > 0
        ? 'senchado-dougu__artisan-count'
        : 'senchado-dougu__artisan-count senchado-dougu__artisan-count--empty';
      var meta = a.prefecture || a.craft
        ? '<div class="senchado-dougu__artisan-meta">' +
            escapeHtml(a.prefecture) +
            (a.prefecture && a.craft ? ' / ' : '') +
            escapeHtml(a.craft) +
          '</div>'
        : '';
      var portrait = a.portrait
        ? '<img class="senchado-dougu__artisan-portrait" src="' + escapeAttr(a.portrait) +
            '" alt="" loading="lazy" decoding="async" width="200" height="200">'
        : '<span class="senchado-dougu__artisan-portrait senchado-dougu__artisan-portrait--empty" aria-hidden="true">' +
            escapeHtml(a.name.slice(0, 1)) + '</span>';
      // href は実在URL（/collections/dougu/author_{タグ}）にする。
      // 2026-08-05まで /collections/{slug} を書いており全件404だった —— クリックは JS が
      // 奪うので気づけないが、中クリック・新規タブ・URL共有・クローラでは死んでいた。
      var href = self.artisanLinkBase + '/author_' + encodeURIComponent(a.linkTag || a.slug);
      return '<a class="senchado-dougu__artisan-card' + (isActive ? ' is-active' : '') +
          '" href="' + escapeAttr(href) + '"' +
          ' data-artisan-card="' + escapeAttr(a.slug) + '"' +
          (isActive ? ' aria-pressed="true"' : ' aria-pressed="false"') + '>' +
          portrait +
          '<div class="senchado-dougu__artisan-name">' + escapeHtml(a.name) + '</div>' +
          meta +
          '<div class="' + countClass + '">' + escapeHtml(countLabel) + '</div>' +
        '</a>';
    }).join('');
  };

  // ---------- 用途ピル（Liquid描画済みDOMへ件数/active/dim を注釈） ----------
  DouguEngine.prototype.renderPurposePills = function () {
    if (!this.purposeRail) return;
    var self = this;
    var pills = this.purposeRail.querySelectorAll('[data-purpose-pill]');
    pills.forEach(function (pill) {
      var label = pill.dataset.purposePill;
      // 件数 = 作家絞込＋在庫トグルを反映した、その用途の商品数（ファセット）
      var count = 0;
      self.products.forEach(function (p) {
        if (!self.matchesArtisan(p)) return;
        if (!self.matchesMaterial(p)) return;
        if (!self.matchesKanaRow(p)) return;
        if (!self.matchesSearch(p)) return;
        if (self.availableOnly && !p.available) return;
        var hit = (label === UNCLASSIFIED) ? p.purposes.length === 0 : p.purposes.indexOf(label) !== -1;
        if (hit) count += 1;
      });
      var countEl = pill.querySelector('[data-pill-count]');
      if (countEl) countEl.textContent = count > 0 ? String(count) : '';
      pill.classList.toggle('is-active', label === self.activePurpose);
      pill.classList.toggle('is-dimmed', count === 0 && label !== self.activePurpose);
      pill.setAttribute('aria-pressed', label === self.activePurpose ? 'true' : 'false');
      if (label === UNCLASSIFIED) {
        // 未分類はデータが存在する時だけ出す（spec エッジ: 未タグ商品を到達可能に）
        pill.hidden = (count === 0 && self.activePurpose !== UNCLASSIFIED);
      }
    });
  };

  // ---------- 素材ピル（用途と同パターン・basis=用途/作家/在庫を反映） ----------
  DouguEngine.prototype.renderSozaiPills = function () {
    if (!this.sozaiRail) return;
    var self = this;
    var pills = this.sozaiRail.querySelectorAll('[data-sozai-pill]');
    pills.forEach(function (pill) {
      var label = pill.dataset.sozaiPill;
      var count = 0;
      self.products.forEach(function (p) {
        if (!self.matchesArtisan(p)) return;
        if (!self.matchesPurpose(p)) return;
        if (!self.matchesKanaRow(p)) return;
        if (!self.matchesSearch(p)) return;
        if (self.availableOnly && !p.available) return;
        var hit = (label === UNCLASSIFIED) ? p.materials.length === 0 : p.materials.indexOf(label) !== -1;
        if (hit) count += 1;
      });
      var countEl = pill.querySelector('[data-pill-count]');
      if (countEl) countEl.textContent = count > 0 ? String(count) : '';
      pill.classList.toggle('is-active', label === self.activeMaterial);
      pill.classList.toggle('is-dimmed', count === 0 && label !== self.activeMaterial);
      pill.setAttribute('aria-pressed', label === self.activeMaterial ? 'true' : 'false');
      if (label === UNCLASSIFIED) {
        pill.hidden = (count === 0 && self.activeMaterial !== UNCLASSIFIED);
      }
    });
  };

  DouguEngine.prototype.updateBreadcrumbLeaf = function () {
    if (!this.bcLeaf) return; // 静的leaf指定ページではJSは触らない（Liquidが要素を出さない）
    var parts = [];
    // 作家を先頭に置く。作家カードで絞り込んだとき「いま誰を見ているか」がパンくずに出る
    if (this.activeArtisan) {
      var self = this;
      var hit = this.products.filter(function (p) { return p.artisanSlug === self.activeArtisan; })[0];
      if (hit && hit.artisanName) parts.push(hit.artisanName);
    } else if (this.activeKanaRow) {
      parts.push(this.activeKanaRow + '行の作家');
    }
    if (this.activePurpose) parts.push(this.activePurpose);
    if (this.activeMaterial) parts.push(this.activeMaterial);
    if (parts.length) {
      this.bcLeaf.textContent = parts.join(' / ');
      if (this.bcSep) this.bcSep.hidden = false;
    } else {
      this.bcLeaf.textContent = '';
      if (this.bcSep) this.bcSep.hidden = true;
    }
  };

  // ---------- VIEW (filter + sort + partition) ----------
  DouguEngine.prototype.applyView = function () {
    var self = this;

    var visible = this.products.filter(function (p) {
      if (!self.matchesPurpose(p)) return false;
      if (!self.matchesMaterial(p)) return false;
      if (!self.matchesArtisan(p)) return false;
      if (!self.matchesKanaRow(p)) return false;
      if (!self.matchesSearch(p)) return false;
      if (self.availableOnly && !p.available) return false;
      return true;
    });

    // 並び替え（各グループ内でこの順を維持=stable）
    var mode = this.sortMode;
    var ordered;

    if (mode === 'default' && this.dailyOrder) {
      // 既定＝日替わり。発売枠→ピン→ローテ→重み付き抽選→売切、を一括で返す。
      // 在庫あり優先の分割も senchado-shuffle.js の中でやるので、ここでは分けない。
      // 絞り込み中でも同じ規則を適用する（母集団が縮むだけ）。
      ordered = window.SenchadoShuffle.orderProducts(visible, {
        handle: this.collectionHandle
      });
    } else {
      if (mode === 'price-asc') {
        visible.sort(function (a, b) { return a.price - b.price; });
      } else if (mode === 'price-desc') {
        visible.sort(function (a, b) { return b.price - a.price; });
      } else if (mode === 'name') {
        visible.sort(function (a, b) { return a.title.localeCompare(b.title, 'ja'); });
      } else {
        // 新着順（created 降順）。日替わりが無効な面（セット・ギフトボックス）の既定でもある。
        // ⚠️ created は「商品ページを作った日」で発売日ではない。道具では実測で
        //    中央値637日ズレる（だから道具の既定を日替わりに移した）。
        visible.sort(function (a, b) { return b.created - a.created; });
      }

      // 在庫あり優先・売切は下部（availableOnly時は全て在庫あり）
      var availArr = [], soldArr = [];
      visible.forEach(function (p) { (p.available ? availArr : soldArr).push(p); });
      ordered = availArr.concat(soldArr);
    }

    // 窓表示: マッチ全件(ordered)のうち先頭 visibleLimit 件だけ実表示。残りは is-hidden。
    // DOM/索引には全件残る＝作家集計・ピル件数・件数表示は全件基準のまま（表示だけ窓化）。
    // バックフィル進行中で ordered が visibleLimit 未満なら、あるだけ表示（読み込み中表示が補完）。
    var windowed = ordered.slice(0, this.visibleLimit);
    var shown = new Set(windowed);
    this.products.forEach(function (p) { p.el.classList.toggle('is-hidden', !shown.has(p)); });
    windowed.forEach(function (p, i) { p.el.style.order = i; });

    // 件数は従来どおり総マッチ件数（窓の外も数える）
    if (this.visibleCountEl) this.visibleCountEl.textContent = ordered.length + '件';
    if (this.emptyState) this.emptyState.classList.toggle('is-visible', ordered.length === 0);

    this.updateLoadMore(ordered.length);
  };

  // ---------- 窓表示: もっと見る（ボタン逐次・自動無限スクロールなし） ----------
  // 総マッチ件数と visibleLimit の差＝残数。残があればボタンを出し（残数を注釈）、全件表示で隠す。
  // スクロールで勝手に増えることはない＝増えるのはボタン押下時のみ（2026-07-07 谷本実機決裁）。
  DouguEngine.prototype.updateLoadMore = function (matchedCount) {
    if (!this.loadMoreEl) return;
    var remaining = matchedCount - this.visibleLimit;
    if (remaining > 0) {
      this.loadMoreEl.hidden = false;
      if (this.loadMoreCountEl) this.loadMoreCountEl.textContent = '（残り' + remaining + '件）';
    } else {
      this.loadMoreEl.hidden = true;
    }
  };

  // 「もっと見る」押下＝1回で LOAD_STEP(40) 件を追加表示する。残りがあれば updateLoadMore が
  // ボタンを再表示するので、全件表示に達するまで押すたびにボタンが出る（スクロールでは増えない）。
  DouguEngine.prototype.loadMore = function () {
    this.visibleLimit += LOAD_STEP;
    this.applyView();
  };

  // ---------- EVENTS ----------
  DouguEngine.prototype.bindEvents = function () {
    var self = this;

    if (this.sortEl) {
      this.sortEl.addEventListener('change', function () {
        self.sortMode = self.sortEl.value;
        self.visibleLimit = VISIBLE_STEP; // 並び替えで窓を24件にリセット（初期24件に戻す）
        self.applyView(); // ソートはレール/ピルに影響しない
      });
    }

    if (this.availableOnlyEl) {
      this.availableOnlyEl.addEventListener('change', function () {
        self.availableOnly = !!self.availableOnlyEl.checked;
        self.visibleLimit = VISIBLE_STEP; // 在庫トグルで窓をリセット（初期24件に戻す）
        self.refresh();
      });
    }

    if (this.artisanSortEl) {
      this.artisanSortEl.value = this.artisanSort;
      this.artisanSortEl.addEventListener('change', function () {
        self.artisanSort = self.artisanSortEl.value === 'stock' ? 'stock' : 'kana';
        self.refresh(); // 作家レールの並びだけが変わる（グリッドは不変）
      });
    }

    if (this.searchEl) {
      // 入力ごとに全再描画すると作家レール96枚の再構築でもたつくので短く間引く。
      // 打鍵が止まって SEARCH_DEBOUNCE_MS 後に1回だけ走る。
      var SEARCH_DEBOUNCE_MS = 140;
      var timer = null;
      var run = function () {
        self.setSearch(self.searchEl.value);
        self.visibleLimit = VISIBLE_STEP; // 検索語の変更で窓をリセット（初期24件に戻す）
        self.refresh();
      };
      this.searchEl.addEventListener('input', function () {
        if (timer) clearTimeout(timer);
        timer = setTimeout(run, SEARCH_DEBOUNCE_MS);
      });
      // Enter と ×（type=search のネイティブクリア）は待たずに即反映
      this.searchEl.addEventListener('search', function () {
        if (timer) clearTimeout(timer);
        run();
      });
      this.searchEl.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault(); // フォーム内でも送信させない（ページ遷移するとページ内絞込が消える）
        if (timer) clearTimeout(timer);
        run();
      });
    }

    this.root.addEventListener('click', function (e) {
      var t;
      if ((t = e.target.closest('[data-purpose-pill]'))) {
        var label = t.dataset.purposePill;
        self.activePurpose = (self.activePurpose === label) ? null : label;
        self.visibleLimit = VISIBLE_STEP; // 用途変更で窓をリセット（初期24件に戻す）
        self.refresh();
      } else if ((t = e.target.closest('[data-sozai-pill]'))) {
        var slabel = t.dataset.sozaiPill;
        self.activeMaterial = (self.activeMaterial === slabel) ? null : slabel;
        self.visibleLimit = VISIBLE_STEP; // 素材変更で窓をリセット（初期24件に戻す）
        self.refresh();
      } else if ((t = e.target.closest('[data-artisan-card]')) && self.artisanAction === 'filter') {
        // in-pageフィルタ（既定）。作家の実コレクション作成後は data-artisan-action="navigate" で遷移へ
        e.preventDefault();
        var slug = t.dataset.artisanCard;
        self.activeArtisan = (self.activeArtisan === slug) ? null : slug;
        self.visibleLimit = VISIBLE_STEP; // 作家変更で窓をリセット（初期24件に戻す）
        self.refresh();
      } else if ((t = e.target.closest('[data-kana-row]'))) {
        var krow = t.dataset.kanaRow;
        self.activeKanaRow = (self.activeKanaRow === krow) ? null : krow;
        self.activeArtisan = null;   // 行を変えたら作家の選択は持ち越さない
        self.visibleLimit = VISIBLE_STEP; // 50音変更で窓をリセット（初期24件に戻す）
        self.refresh();
      } else if ((t = e.target.closest('[data-search-clear]'))) {
        if (self.searchEl) self.searchEl.value = '';
        self.setSearch('');
        self.visibleLimit = VISIBLE_STEP;
        self.refresh();
        if (self.searchEl) self.searchEl.focus();
      } else if ((t = e.target.closest('[data-clear-filters]'))) {
        self.activePurpose = null;
        self.activeMaterial = null;
        self.activeArtisan = null;
        self.activeKanaRow = null;
        self.availableOnly = false;
        if (self.availableOnlyEl) self.availableOnlyEl.checked = false;
        if (self.searchEl) self.searchEl.value = '';
        self.setSearch('');
        self.visibleLimit = VISIBLE_STEP; // クリアで窓をリセット（初期24件に戻す）
        self.refresh();
      } else if ((t = e.target.closest('[data-load-more]'))) {
        self.loadMore();
      } else if ((t = e.target.closest('[data-add-to-cart]'))) {
        e.preventDefault();
        self.addToCart(t);
      }
    });
  };

  // ---------- CART ----------
  DouguEngine.prototype.addToCart = function (button) {
    // 先行発売ロック（監査 L41）: Cart Function無効（Plus必須）でサーバーガードが無いため、
    // disabled が外されてもここで必ず /cart/add.js を止める。
    if (button.dataset.earlyLocked === 'true') return;
    var id = button.dataset.variantId;
    if (!id || button.disabled) return;
    button.disabled = true;
    button.dataset.state = 'loading';
    var original = button.textContent;
    button.textContent = '追加中…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    }).then(function (res) {
      if (!res.ok) throw new Error('カートに追加できませんでした');
      return res.json();
    }).then(function () {
      button.dataset.state = 'success';
      button.textContent = 'カートに追加しました';
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      setTimeout(function () {
        button.disabled = false;
        delete button.dataset.state;
        button.textContent = original;
      }, 2000);
    }).catch(function () {
      button.disabled = false;
      delete button.dataset.state;
      button.textContent = original;
      alert('カートに追加できませんでした。時間をおいて再度お試しください。');
    });
  };

  // ---------- BOOT ----------
  function boot() {
    document.querySelectorAll('[data-senchado-dougu]').forEach(function (root) {
      if (root.dataset.engineBooted) return;
      root.dataset.engineBooted = '1';
      try {
        new DouguEngine(root);
      } catch (err) {
        if (window.console) console.error('[senchado-dougu]', err);
        root.dataset.engine = 'ready';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
